/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable camelcase */
import { DateTime } from 'luxon';
import { Store } from './Store';
import { flatten } from '../lib';
import { TrackingResource } from '../api';
import { getWorkspaceAliasFromUrl } from '../lib/workspace';
import { skipTracking } from '../lib/tracking';
import { environment } from './environment';
import _ from 'lodash';

declare global {
    interface Window {
        analytics: any;
        aludeCookies: any;
    }
}

declare const gtag: (op: string, target: string, options: object) => void;
declare const fbq: (
    op: string,
    target: string,
    options: object,
    data: object
) => void;

let hasIdentified = false;

function firstLastName(name?: string) {
    const parts = (name || '').replace(/\s+/g, ' ').split(' ');
    return parts.length < 2
        ? {}
        : { first_name: parts[0], last_name: parts[parts.length - 1] };
}

function getDaysAfterSignUp(accCreatedAt?: DateTime) {
    return accCreatedAt
        ? Math.round(DateTime.now().diff(accCreatedAt, ['days']).days)
        : undefined;
}

async function getAccountPayload(store?: Store) {
    if (!store) return null;

    const account = await (store.account.account
        ? Promise.resolve(store.account.account)
        : store.account.loadAccount());

    if (!account) return null;
    return {
        id: account.id,
        email: account.email,
        name: account.name,
        phone: account.phone,
        email_verified_at:
            account.emailVerifiedAt && account.emailVerifiedAt.toISO(),
        phone_verified_at:
            account.phoneVerifiedAt && account.phoneVerifiedAt.toISO(),
        created_at: account.createdAt.toISO(),
        days_after_sign_up: getDaysAfterSignUp(account.createdAt),
        ...firstLastName(account.name),
    };
}

function getWorkspacePayload(store: Store) {
    const workspaceAlias = getWorkspaceAliasFromUrl();
    return store.workspace
        .getAsyncWorkspace(workspaceAlias ? parseInt(workspaceAlias) : null)
        .then(workspace => ({
            id: workspace?.id,
            name: workspace?.name,
            email: workspace?.email,
            creci: workspace?.creci,
            has_logo: workspace?.logoS3Path !== null,
        }));
}

function getBasicEventPayload(store: Store) {
    return getAccountPayload(store)
        .then(account => {
            return getWorkspacePayload(store).then(workspace => {
                return {
                    account,
                    workspace,
                };
            });
        })
        .then(({ account, workspace }) => ({
            authed_account: account,
            workspace: workspace,
        }));
}

function getCampaign() {
    const utmstr = window.aludeCookies.getCookie('_utm') || '';
    return utmstr
        .split('&')
        .reduce((acc: Record<string, string>, param: string) => {
            const [key, value] = param.split('=');
            if (key.startsWith('utm_')) {
                acc[key.replace('utm_', '')] = value;
            }
            return acc;
        }, {});
}

type EventExtra = Record<string, unknown>;
type Tracker = (eventName: string, properties: unknown) => void;

// eslint-disable-next-line max-params
function track(
    store: Store,
    eventName: string,
    extra?: EventExtra,
    external: boolean = false,
    trackers: Tracker[] = [TrackingResource.track],
    trackersExternal: Tracker[] = [TrackingResource.trackExternal]
): PromiseLike<void> {
    // Disable tracking for admin login
    if (skipTracking()) {
        return Promise.resolve();
    }
    if (external) {
        trackersExternal.map(tracker => tracker(eventName, extra));
        return Promise.resolve();
    }

    return getBasicEventPayload(store).then(basicData => {
        trackers.map(tracker =>
            tracker(eventName, flatten({ ...basicData, ...extra }))
        );
    });
}

async function identify(store?: Store): Promise<void> {
    const account = await getAccountPayload(store);
    if (account !== null && !hasIdentified && window.aludeCookies) {
        window.aludeCookies.setFBIdentify({
            em: account.email,
            fn: account.first_name,
            ln: account.last_name,
            ph: account.phone,
            external_id: account.id,
        });
        gtag('set', 'user_properties', {
            name: account.name,
            email: account.email,
        });
        hasIdentified = true;
    }
}

const registerAludePageView = _.debounce(() => {
    environment.TrackingResource?.pageView({
        id: `alude-${new Date().getTime()}-${crypto.randomUUID()}`,
        timestamp: DateTime.now().toISO(),
        campaign: getCampaign(),
        library: {
            name: 'Alude',
            version: '1.0.0',
        },
        locale: navigator.languages
            ? navigator.languages[0]
            : navigator.language,
        page: {
            path: window.location.pathname,
            referrer: document.referrer !== '' ? document.referrer : null,
            search:
                window.location.search !== '' ? window.location.search : null,
            title: document.title,
            url: window.location.href,
        },
        timezone: DateTime.local().zoneName,
        fbc: window.aludeCookies.getCookie('_fbc') ?? null,
        fbp: window.aludeCookies.getCookie('_fbp') ?? null,
        utmstr: window.aludeCookies.getCookie('_utm') ?? null,
    });
}, 1000);

async function page(store?: Store): Promise<void> {
    await identify(store);
    fbq(
        'track',
        'PageView',
        {},
        { eventID: `alude-${new Date().getTime()}-${crypto.randomUUID()}` }
    );
    registerAludePageView();
}

export { track, identify, page };
